import styled from '@emotion/styled';
import {
  Box,
  LinearProgress as Bar,
  Typography,
  type LinearProgressProps,
  linearProgressClasses,
} from '@mui/material';
import { Text } from 'components/text';

export const LinearProgressWithLabel = (props: LinearProgressProps & { value: number }) => {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      <Box sx={{ width: '100%', mr: 1 }}>
        <Bar variant="determinate" {...props} />
      </Box>
      <Box sx={{ minWidth: 35 }}>
        <Typography variant="bodySmall" color="text.secondary">{`${Math.round(
          props.value
        )}%`}</Typography>
      </Box>
    </Box>
  );
};

export const LinearProgress = (props: LinearProgressProps & { value: number }) => {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      <Box sx={{ width: '100%', mr: 1 }}>
        <Bar variant="determinate" {...props} />
      </Box>
    </Box>
  );
};

export interface ProgressBarProps extends LinearProgressProps {
  value?: number;
  currentValue?: number;
  totalValue?: number;
  label?: React.ReactNode;
  hideOutOf?: boolean;
  hideOutOfLabel?: React.ReactNode;
}

export const ProgressBar = ({
  label,
  currentValue = 0,
  totalValue = 0,
  hideOutOf = false,
  hideOutOfLabel,
  value: providedValue,
  ...rest
}: ProgressBarProps) => {
  const value = providedValue ?? (totalValue > 0 ? (currentValue / totalValue) * 100 : 0);

  return (
    <ProgressBarContainer>
      <ProgressBarHeader>
        <Text color="inherit" weight="semiBold">
          {label}
        </Text>
        {!hideOutOf && (
          <Text color="inherit" weight="semiBold">
            {hideOutOfLabel ?? `${currentValue}/${totalValue}`}
          </Text>
        )}
      </ProgressBarHeader>
      <BorderLinearProgress {...rest} value={value} />
    </ProgressBarContainer>
  );
};

const ProgressBarContainer = styled.div`
  display: flex;
  width: 100%;
  flex-direction: column;
  gap: 4px;
`;

const ProgressBarHeader = styled.div`
  display: flex;
  justify-content: space-between;
  color: ${({ theme }) => theme.palette.text.secondary};
`;

export const BorderLinearProgress = styled(LinearProgress)(({ theme }) => ({
  height: 8,
  borderRadius: 6,
  [`&.${linearProgressClasses.colorPrimary}`]: {
    backgroundColor: theme.palette.utility.grey[100],
  },
  [`& .${linearProgressClasses.bar}`]: {
    borderRadius: 6,
    backgroundColor: theme.palette.fg.infoPrimary,
  },
}));
